import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
  Breadcrumb,
  BreadcrumbContext,
  BreadcrumbProvider,
  BreadcrumbSubheading,
  BreadcrumbSubheadingProps
} from '../../../common/breadcrumb';
import { flattenTree } from '../../../common/flattenTree.helper';
import { FileDto } from '../Document.interface';
import { useGetFileTreeById } from '../Document.hook';

export interface DocumentBreadcrumbsProps extends BreadcrumbSubheadingProps {
  documentId: number;
}

const DocumentBreadcrumbs: React.FC<DocumentBreadcrumbsProps> = (
  props: DocumentBreadcrumbsProps
) => {
  return (
    <BreadcrumbProvider>
      <DocumentBreadcrumbsRoot {...props} />
    </BreadcrumbProvider>
  );
};

export const DocumentBreadcrumbsRoot: React.FC<DocumentBreadcrumbsProps> = (
  props: DocumentBreadcrumbsProps
) => {
  const { documentId, ...breadcrumProps } = props;
  const { clearBreadcrumbs, updateBreadcrumb } = useContext(BreadcrumbContext);
  const [root, setRoot] = useState<FileDto | undefined>();
  const [{ data }, getTree] = useGetFileTreeById(documentId);

  const flatList: FileDto[] = useMemo(() => {
    const flatList: FileDto[] = [];
    if (root) {
      flattenTree(root, flatList, ({ child, hasValidLeafNode }) => {
        return hasValidLeafNode || child.id === documentId;
      });
    }
    return flatList;
  }, [root]);

  useEffect(() => {
    getTree();
  }, [getTree]);

  useEffect(() => {
    if (data) {
      const root: FileDto | undefined = data.find(
        (file: FileDto) => file.children && file.children.length > 0
      );
      setRoot(root);
    }
  }, [data]);

  useEffect(() => {
    clearBreadcrumbs();
    flatList.forEach((file: FileDto, index: number) => {
      const breadcrumb: Breadcrumb = {
        index,
        label: file.isFolder
          ? file.name || ''
          : file.task?.key || `DOC-${file.id}`,
        path: file.isFolder
          ? `/portal/documents/folder/${file.id}`
          : `/portal/documents/file/task/${file.taskId}`
      };
      updateBreadcrumb(index, breadcrumb);
    });
  }, [flatList, clearBreadcrumbs, updateBreadcrumb]);

  return <BreadcrumbSubheading {...breadcrumProps} />;
};

export default DocumentBreadcrumbs;
